import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { softPlatformBarClass, softPlatformIcon } from './socialSoftPalette';
import { LinkedInIcon, FacebookIcon, InstagramIcon, TwitterIcon, AnalyticsIcon, CalendarIcon, GrowthIcon, TrophyIcon, WellnessIcon, RetryIcon, ChatIcon, ViewIcon, SuccessIcon, ClockIcon, QueueIcon, TipIcon, EmptyTrayIcon } from '../icons';

interface ShareData {
  id: string;
  platform: string;
  content: string;
  scheduledFor: Date;
  publishedAt?: Date;
  status: 'scheduled' | 'published' | 'failed';
  engagement?: {
    likes: number;
    shares: number;
    comments: number;
    impressions: number;
  };
}

interface PlatformStats {
  platform: string;
  totalPosts: number;
  totalEngagement: number;
  avgEngagement: number;
}

interface SocialAnalyticsProps {
  userId: string;
}

const SocialAnalytics: React.FC<SocialAnalyticsProps> = ({ userId }) => {
  const [shares, setShares] = useState<ShareData[]>([]);
  const [timeRange, setTimeRange] = useState<'week' | 'month' | 'all'>('month');
  const [loading, setLoading] = useState(true);

  const platforms = [
    { id: 'linkedin', name: 'LinkedIn', Icon: LinkedInIcon, iconColor: softPlatformIcon('linkedin'), barClass: softPlatformBarClass('linkedin') },
    { id: 'facebook', name: 'Facebook', Icon: FacebookIcon, iconColor: softPlatformIcon('facebook'), barClass: softPlatformBarClass('facebook') },
    { id: 'instagram', name: 'Instagram', Icon: InstagramIcon, iconColor: softPlatformIcon('instagram'), barClass: softPlatformBarClass('instagram') },
    { id: 'twitter', name: 'Twitter/X', Icon: TwitterIcon, iconColor: softPlatformIcon('twitter'), barClass: softPlatformBarClass('twitter') },
  ];

  useEffect(() => {
    loadAnalytics();
  }, [userId, timeRange]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const now = new Date();
      let startDate = new Date();
      
      if (timeRange === 'week') {
        startDate.setDate(now.getDate() - 7);
      } else if (timeRange === 'month') {
        startDate.setMonth(now.getMonth() - 1);
      } else {
        startDate.setFullYear(now.getFullYear() - 10); // All time
      }

      const sharesQuery = query(
        collection(db, 'scheduledPosts'),
        where('userId', '==', userId),
        where('status', 'in', ['scheduled', 'published']),
        orderBy('scheduledFor', 'desc')
      );

      const snapshot = await getDocs(sharesQuery);
      const loadedShares = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        scheduledFor: doc.data().scheduledFor.toDate(),
        publishedAt: doc.data().publishedAt?.toDate()
      })) as ShareData[];

      // Filter by time range
      const filteredShares = loadedShares.filter(share => {
        const shareDate = share.publishedAt || share.scheduledFor;
        return shareDate >= startDate;
      });

      setShares(filteredShares);
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculatePlatformStats = (): PlatformStats[] => {
    const stats: Record<string, PlatformStats> = {};

    platforms.forEach(platform => {
      const platformShares = shares.filter(share => 
        share.platform === platform.id
      );

      const totalEngagement = platformShares.reduce((sum, share) => {
        return sum + (share.engagement?.likes || 0) + 
               (share.engagement?.shares || 0) + 
               (share.engagement?.comments || 0);
      }, 0);

      stats[platform.id] = {
        platform: platform.name,
        totalPosts: platformShares.length,
        totalEngagement,
        avgEngagement: platformShares.length > 0 ? Math.round(totalEngagement / platformShares.length) : 0
      };
    });

    return Object.values(stats);
  };

  const getTotalMetrics = () => {
    const published = shares.filter(s => s.status === 'published');
    const totalEngagement = published.reduce((sum, share) => {
      return sum + (share.engagement?.likes || 0) + 
             (share.engagement?.shares || 0) + 
             (share.engagement?.comments || 0);
    }, 0);

    const totalImpressions = published.reduce((sum, share) => {
      return sum + (share.engagement?.impressions || 0);
    }, 0);

    return {
      totalPosts: published.length,
      scheduledPosts: shares.filter(s => s.status === 'scheduled').length,
      totalEngagement,
      totalImpressions,
      avgEngagement: published.length > 0 ? Math.round(totalEngagement / published.length) : 0
    };
  };

  const metrics = getTotalMetrics();
  const platformStats = calculatePlatformStats();
  const topPost = shares
    .filter(s => s.engagement)
    .sort((a, b) => {
      const aEng = (a.engagement?.likes || 0) + (a.engagement?.shares || 0) + (a.engagement?.comments || 0);
      const bEng = (b.engagement?.likes || 0) + (b.engagement?.shares || 0) + (b.engagement?.comments || 0);
      return bEng - aEng;
    })[0];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#7A9488] mx-auto mb-4"></div>
          <p className="text-gray-600">Analytics laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 max-w-5xl mx-auto">
      <div className="mb-5 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <AnalyticsIcon size={20} weight="regular" className="text-gray-600" /> Social Media Analytics
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">Inzicht in je social media performance</p>
        </div>

        {/* Time Range Filter */}
        <div className="flex gap-1.5">
          {(['week', 'month', 'all'] as const).map(range => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                timeRange === range
                  ? 'bg-[#7A9488] text-white'
                  : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              <CalendarIcon size={14} weight="regular" className="mr-0.5" />
              {range === 'week' ? 'Afgelopen Week' : range === 'month' ? 'Afgelopen Maand' : 'Alles'}
            </button>
          ))}
        </div>
      </div>

      {/* Key Metrics — sk-analytics-kpi-tile: exempt from .quiet-lounge .font-bold / .opacity-90 text overrides (contrast) */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <div className="sk-analytics-kpi-tile bg-gradient-to-br from-[#7A9488] to-[#4F6660] rounded-lg p-4 text-white">
          <div className="text-xs font-medium sk-analytics-kpi-sub">Totaal Posts</div>
          <div className="text-2xl font-bold mt-1 tabular-nums">{metrics.totalPosts}</div>
          <div className="text-[10px] mt-1 sk-analytics-kpi-dim">Gepubliceerd</div>
        </div>

        <div className="sk-analytics-kpi-tile bg-gradient-to-br from-[#6B6560] to-[#524E49] rounded-lg p-4 text-white">
          <div className="text-xs font-medium sk-analytics-kpi-sub">Ingepland</div>
          <div className="text-2xl font-bold mt-1 tabular-nums">{metrics.scheduledPosts}</div>
          <div className="text-[10px] mt-1 sk-analytics-kpi-dim">Nog te publiceren</div>
        </div>

        <div className="sk-analytics-kpi-tile bg-gradient-to-br from-[#6D8578] to-[#556B60] rounded-lg p-4 text-white">
          <div className="text-xs font-medium sk-analytics-kpi-sub">Totaal Engagement</div>
          <div className="text-2xl font-bold mt-1 tabular-nums">{metrics.totalEngagement}</div>
          <div className="text-[10px] mt-1 sk-analytics-kpi-dim">Likes + Shares + Comments</div>
        </div>

        <div className="sk-analytics-kpi-tile bg-gradient-to-br from-[#8DA8A0] to-[#6B8B7E] rounded-lg p-4 text-white">
          <div className="text-xs font-medium sk-analytics-kpi-sub">Gem. Engagement</div>
          <div className="text-2xl font-bold mt-1 tabular-nums">{metrics.avgEngagement}</div>
          <div className="text-[10px] mt-1 sk-analytics-kpi-dim">Per post</div>
        </div>

        <div className="sk-analytics-kpi-tile bg-gradient-to-br from-[#7A8FA0] to-[#5F7585] rounded-lg p-4 text-white">
          <div className="text-xs font-medium sk-analytics-kpi-sub">Impressies</div>
          <div className="text-2xl font-bold mt-1 tabular-nums">{metrics.totalImpressions}</div>
          <div className="text-[10px] mt-1 sk-analytics-kpi-dim">Totaal bereik</div>
        </div>
      </div>

      {/* Platform Performance */}
      <div className="mb-6">
        <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-1.5">
          <GrowthIcon size={18} weight="regular" className="text-gray-600" /> Performance per Platform
        </h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {platformStats.map(stat => {
            const platform = platforms.find(p => p.name === stat.platform);
            return (
              <div key={stat.platform} className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex items-center mb-3">
                  {platform?.Icon && <platform.Icon size={24} weight="regular" style={{ color: platform.iconColor }} className="mr-2" />}
                  <div>
                    <div className="font-medium text-gray-900 text-sm">{stat.platform}</div>
                    <div className="text-[10px] text-gray-500">{stat.totalPosts} posts</div>
                  </div>
                </div>
                
                <div className="space-y-1.5">
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500">Totaal Engagement:</span>
                    <span className="font-semibold text-gray-900">{stat.totalEngagement}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500">Gemiddeld:</span>
                    <span className="font-semibold text-gray-900">{stat.avgEngagement}</span>
                  </div>
                </div>

                {/* Visual Bar */}
                <div className="mt-3">
                  <div className="w-full bg-gray-100 rounded-full h-1.5">
                    <div 
                      className={`${platform?.barClass} h-1.5 rounded-full transition-all`}
                      style={{ 
                        width: `${Math.min((stat.totalEngagement / Math.max(...platformStats.map(s => s.totalEngagement))) * 100, 100)}%` 
                      }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Top Performing Post */}
      {topPost && topPost.engagement && (
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-1.5">
            <TrophyIcon size={18} weight="regular" className="text-yellow-600" /> Best Presterende Post
          </h3>
          <div className="bg-gradient-to-r from-stone-50 to-stone-100 rounded-lg border border-stone-200 p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-2">
                {(() => {
                  const platform = platforms.find(p => p.id === topPost.platform);
                  return platform?.Icon ? (
                    <platform.Icon size={22} weight="regular" style={{ color: platform.iconColor }} />
                  ) : null;
                })()}
              </div>
              <div className="flex items-center gap-3 text-xs">
                <div className="flex items-center gap-0.5">
                  <WellnessIcon size={14} weight="regular" className="text-[#9A9A9A]" />
                  <span className="font-semibold">{topPost.engagement.likes}</span>
                </div>
                <div className="flex items-center gap-0.5">
                  <RetryIcon size={14} weight="regular" className="text-gray-500" />
                  <span className="font-semibold">{topPost.engagement.shares}</span>
                </div>
                <div className="flex items-center gap-0.5">
                  <ChatIcon size={14} weight="regular" className="text-gray-500" />
                  <span className="font-semibold">{topPost.engagement.comments}</span>
                </div>
                <div className="flex items-center gap-0.5">
                  <ViewIcon size={14} weight="regular" className="text-gray-500" />
                  <span className="font-semibold">{topPost.engagement.impressions}</span>
                </div>
              </div>
            </div>
            <p className="text-sm text-gray-700 whitespace-pre-wrap line-clamp-3">{topPost.content}</p>
            {topPost.publishedAt && (
              <div className="text-[10px] text-gray-500 mt-2">
                Gepubliceerd op {topPost.publishedAt.toLocaleDateString('nl-NL')}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Recent Activity */}
      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-1.5">
          <QueueIcon size={18} weight="regular" className="text-gray-600" /> Recente Activiteit
        </h3>
        <div className="space-y-2">
          {shares.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <AnalyticsIcon size={36} weight="regular" className="mx-auto mb-3 text-gray-400" />
              <h4 className="text-sm font-medium text-gray-700 mb-1">Nog geen data beschikbaar</h4>
              <p className="text-xs text-gray-500">
                Begin met het delen van posts om je analytics te zien
              </p>
            </div>
          ) : (
            shares.slice(0, 10).map(share => (
              <div key={share.id} className="bg-white rounded-lg border border-gray-200 p-3 hover:border-gray-300 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1.5">
                      {(() => {
                        const platform = platforms.find(p => p.id === share.platform);
                        return platform?.Icon ? (
                          <platform.Icon size={18} weight="regular" style={{ color: platform.iconColor }} />
                        ) : null;
                      })()}
                      <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded inline-flex items-center gap-0.5 ${
                        share.status === 'published' ? 'bg-[#e8f0ec] text-[#4F6660]' : 'bg-stone-100 text-[#5c5c5c]'
                      }`}>
                        {share.status === 'published' ? <><SuccessIcon size={10} weight="regular" /> Gepubliceerd</> : <><ClockIcon size={10} weight="regular" /> Ingepland</>}
                      </span>
                    </div>
                    <p className="text-xs text-gray-700 line-clamp-2">{share.content}</p>
                    <div className="text-[10px] text-gray-500 mt-1.5">
                      {share.publishedAt 
                        ? `Gepubliceerd: ${share.publishedAt.toLocaleString('nl-NL')}`
                        : `Gepland voor: ${share.scheduledFor.toLocaleString('nl-NL')}`
                      }
                    </div>
                  </div>

                  {share.engagement && (
                    <div className="ml-3 flex gap-2 text-xs">
                      <div className="text-center">
                        <div className="font-semibold text-gray-900">{share.engagement.likes}</div>
                        <WellnessIcon size={12} weight="regular" className="mx-auto text-stone-400" />
                      </div>
                      <div className="text-center">
                        <div className="font-semibold text-gray-900">{share.engagement.shares}</div>
                        <RetryIcon size={12} weight="regular" className="mx-auto text-gray-400" />
                      </div>
                      <div className="text-center">
                        <div className="font-semibold text-gray-900">{share.engagement.comments}</div>
                        <ChatIcon size={12} weight="regular" className="mx-auto text-gray-400" />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Info Note */}
      <div className="mt-6 bg-stone-50 border border-stone-200 rounded-lg p-3">
        <div className="flex items-start">
          <TipIcon size={18} weight="regular" className="mr-2 text-[#7A9488] flex-shrink-0" />
          <div className="text-xs text-stone-600">
            <strong>Note:</strong> Engagement metrics worden automatisch bijgewerkt wanneer de OAuth koppelingen actief zijn.
            Voor demo doeleinden worden momenteel mock data getoond.
          </div>
        </div>
      </div>
    </div>
  );
};

export default SocialAnalytics;


